import { useEffect, useRef } from 'react';
import './LlmNetwork.css';

type Point = { x: number; y: number };
type Route = { id: string; points: [Point, Point, Point, Point]; color: string; opacity: number };
const CORE: Point = { x: 958, y: 414 };
const AGENTS: Point[] = [{ x: 1220, y: 216 }, { x: 1330, y: 400 }, { x: 1268, y: 580 }, { x: 1094, y: 644 }];
const ECOSYSTEM: Point[] = [{ x: 175, y: 635 }, { x: 398, y: 614 }, { x: 620, y: 674 }];
const SERVICE_RADIUS = 74;
type Glyph = 'ai' | 'app' | 'network' | 'dx' | 'cloud' | 'server';
const SERVICES: { label: string; lines: string[]; icon: Glyph }[] = [
  { label: 'AI・LLM・AIエージェント', lines: ['AI・LLM・', 'AIエージェント'], icon: 'ai' },
  { label: 'システム・アプリ開発', lines: ['システム・', 'アプリ開発'], icon: 'app' },
  { label: 'ネットワーク・サーバー構築運用', lines: ['ネットワーク・', 'サーバー', '構築運用'], icon: 'network' },
  { label: 'コンサル・DX', lines: ['コンサル・DX'], icon: 'dx' },
];
const COLORS = ['#4984ed', '#8bbbf0', '#abaff0', '#70a7e7'];
const point = (x: number, y: number): Point => ({ x, y });
const pathData = ({ points: [a, b, c, d] }: Route) => `M${a.x},${a.y} C${b.x},${b.y} ${c.x},${c.y} ${d.x},${d.y}`;
function along([a, b, c, d]: Route['points'], t: number): Point {
  const s = 1 - t;
  return point(s ** 3 * a.x + 3 * s * s * t * b.x + 3 * s * t * t * c.x + t ** 3 * d.x,
    s ** 3 * a.y + 3 * s * s * t * b.y + 3 * s * t * t * c.y + t ** 3 * d.y);
}
function routesFor(agents: Point[], ecosystem = ECOSYSTEM): Route[] {
  const routes: Route[] = [];
  for (let i = 0; i < 6; i++) {
    const y = 344 + i * 60;
    routes.push({ id: `input-${i}`, color: COLORS[i % 4], opacity: .24 + (i % 2) * .12,
      points: [point(-80, y), point(255, y + 245), point(570, 425 + i * 10), point(CORE.x - 148, CORE.y + (i - 2.5) * 6)] });
  }
  agents.forEach((agent, i) => {
    const angle = Math.atan2(agent.y - CORE.y, agent.x - CORE.x);
    const start = point(CORE.x + Math.cos(angle) * 148, CORE.y + Math.sin(angle) * 148);
    const end = point(agent.x - Math.cos(angle) * SERVICE_RADIUS, agent.y - Math.sin(angle) * SERVICE_RADIUS);
    for (let j = 0; j < 4; j++) {
      const offset = (j - 1.5) * 7;
      routes.push({ id: `agent-${i}-${j}`, color: COLORS[j], opacity: j === 0 ? .65 : .36,
        points: [start, point(start.x + (end.x - start.x) * .58, start.y + offset),
          point(end.x - (end.x - start.x) * .46, end.y + offset), end] });
    }
    for (let j = 0; j < 2; j++) {
      routes.push({ id: `output-${i}-${j}`, color: COLORS[j], opacity: .27,
        points: [point(agent.x + SERVICE_RADIUS, agent.y + j * 7), point(agent.x + 160, agent.y + j * 20),
          point(1480, agent.y + (i - 1.5) * 25), point(1550, agent.y + (i - 1.5) * 70)] });
    }
  });
  ecosystem.forEach((node, i) => {
    const next = ecosystem[i + 1];
    const angle = Math.atan2(node.y - CORE.y, node.x - CORE.x);
    const end = next ? point(next.x - 40, next.y) : point(CORE.x + Math.cos(angle) * 148, CORE.y + Math.sin(angle) * 148);
    const start = point(node.x + 40, node.y);
    routes.push({ id: `ecosystem-${i}`, color: COLORS[i % 4], opacity: .46,
      points: [start, point(start.x + (end.x - start.x) * .46, start.y + 35), point(end.x - 75, end.y + 30), end] });
  });
  routes.push({ id: 'ecosystem-cloud-hub', color: COLORS[1], opacity: .32,
    points: [point(ecosystem[0].x + 40, ecosystem[0].y + 10), point(420, 756), point(702, 560), point(840, 503)] });
  return routes;
}
const INITIAL_ROUTES = routesFor(AGENTS);
const NEURONS = [118, 88, 61].flatMap((radius, ring) => Array.from({ length: [14, 11, 8][ring] }, (_, i) => {
  const angle = i / [14, 11, 8][ring] * Math.PI * 2 + ring * .29;
  return point(Math.cos(angle) * radius, Math.sin(angle) * radius);
}));
const SYNAPSES = NEURONS.flatMap((a, i) => NEURONS.slice(i + 1).flatMap((b, j) => {
  const distance = Math.hypot(a.x - b.x, a.y - b.y);
  return distance < 100 && (i + j) % 3 !== 0 ? [{ a, b }] : [];
}));

function TechnologyGlyph({ type }: { type: Glyph }) {
  if (type === 'ai') return <g>
    <path d="M0 -17L-15 -8V10L0 18L15 10V-8ZM0 -17V0M-15 -8L15 10M15 -8L-15 10M0 0V18" />
    {[[0, -17], [-15, -8], [15, -8], [-15, 10], [15, 10], [0, 18], [0, 0]].map(([x, y], i) => <circle key={i} cx={x} cy={y} r="3" fill="#f7faff" />)}
  </g>;
  if (type === 'app') return <g><rect x="-20" y="-17" width="40" height="33" rx="2" /><path d="M-20 -9H20M-9 -14H-8M-4 -14H-3M1 -14H2M-7 -1L-12 4L-7 9M7 -1L12 4L7 9M3 -2L-2 10" /></g>;
  if (type === 'network') return <g><path d="M-1 -12L-13 10M1 -12L13 10M-10 13H10" /><circle cy="-15" r="6" fill="#f7faff" /><circle cx="-15" cy="13" r="6" fill="#f7faff" /><circle cx="15" cy="13" r="6" fill="#f7faff" /></g>;
  if (type === 'dx') return <g><path d="M-15 -5A16 16 0 0 1 16 -2M16 -2L10 -7M16 -2L20 -8M15 5A16 16 0 0 1 -16 2M-16 2L-10 7M-16 2L-20 8" /></g>;
  if (type === 'server') return <g>{[-14, 0, 14].map(y => <g key={y}><rect x="-20" y={y - 5} width="40" height="10" rx="1" /><path d={`M-15 ${y}H-5M10 ${y - 1}V${y + 1}M15 ${y - 1}V${y + 1}`} /></g>)}</g>;
  return <g><path d="M1 7H-13A9 9 0 0 1 -14 -11A13 13 0 0 1 10 -11A9 9 0 0 1 16 -6" /><ellipse cx="11" cy="1" rx="10" ry="4" /><path d="M1 1V17C1 22 21 22 21 17V1M1 9C1 14 21 14 21 9" /></g>;
}

/** SVG keeps the matte illustration sharp and renders even before motion starts. */
export default function LlmNetwork() {
  const host = useRef<HTMLDivElement>(null);
  const clock = useRef(0);

  useEffect(() => {
    const container = host.current;
    const svg = container?.querySelector('svg');
    if (!container || !svg) return;
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mobile = window.matchMedia('(max-width: 700px)');
    const nodes = Array.from(svg.querySelectorAll<SVGGElement>('[data-agent]'));
    const ecosystemNodes = Array.from(svg.querySelectorAll<SVGGElement>('[data-ecosystem]'));
    const paths = Array.from(svg.querySelectorAll<SVGPathElement>('[data-route]'));
    const dots = Array.from(svg.querySelectorAll<SVGCircleElement>('[data-particle]'));
    const lattice = svg.querySelector<SVGGElement>('[data-lattice]');
    const orbit = svg.querySelector<SVGCircleElement>('[data-core-orbit]');
    let frame = 0, last = 0, inView = true, disposed = false;
    const isRunning = () => !disposed && !motion.matches && inView && !document.hidden;
    function draw() {
      const time = clock.current;
      const positions = AGENTS.map((base, i) => point(
        base.x + Math.sin(time * .24 + i * 1.4) * 4 * (motion.matches ? 0 : 1),
        base.y + Math.sin(time * .32 + i * 1.8) * 6 * (motion.matches ? 0 : 1)));
      positions.forEach((p, i) => nodes[i]?.setAttribute('transform', `translate(${p.x} ${p.y})`));
      const ecosystem = ECOSYSTEM.map((base, i) => point(
        base.x + Math.sin(time * .18 + i * 1.4) * 2 * (motion.matches ? 0 : 1),
        base.y + Math.sin(time * .24 + i * 1.8) * 3 * (motion.matches ? 0 : 1)));
      ecosystem.forEach((p, i) => ecosystemNodes[i]?.setAttribute('transform', `translate(${p.x} ${p.y})`));
      const routes = routesFor(positions, ecosystem);
      routes.forEach((route, i) => {
        paths[i]?.setAttribute('d', pathData(route));
        const p = along(route.points, (time / (9 + i % 5) + i * .173) % 1);
        dots[i]?.setAttribute('cx', String(p.x));
        dots[i]?.setAttribute('cy', String(p.y));
      });
      lattice?.setAttribute('transform', `rotate(${Math.sin(time * .1) * 4})`);
      orbit?.setAttribute('stroke-opacity', String(.32 + Math.sin(time * .7) * .1));
    }
    function tick(now: number) {
      frame = 0;
      if (!isRunning()) return;
      if (last && now - last < 32) { frame = requestAnimationFrame(tick); return; }
      if (last) clock.current += Math.min((now - last) / 1000, .08);
      last = now;
      draw();
      frame = requestAnimationFrame(tick);
    }
    function sync() {
      if (frame) cancelAnimationFrame(frame);
      frame = 0; last = 0;
      container!.dataset.motion = motion.matches ? 'reduced' : isRunning() ? 'running' : 'paused';
      if (isRunning()) frame = requestAnimationFrame(tick);
    }
    const onMotion = () => { if (motion.matches) draw(); sync(); };
    const onSize = () => svg.setAttribute('viewBox', mobile.matches ? '740 104 716 638' : '0 0 1440 780');
    const observer = new IntersectionObserver(([entry]) => { inView = entry.isIntersecting; sync(); });
    observer.observe(container);
    document.addEventListener('visibilitychange', sync);
    motion.addEventListener('change', onMotion);
    mobile.addEventListener('change', onSize);
    onSize(); draw(); sync();
    return () => {
      disposed = true;
      if (frame) cancelAnimationFrame(frame);
      observer.disconnect();
      document.removeEventListener('visibilitychange', sync);
      motion.removeEventListener('change', onMotion);
      mobile.removeEventListener('change', onSize);
    };
  }, []);

  return <div ref={host} className="llm-network" data-motion="paused">
    <svg viewBox="0 0 1440 780" preserveAspectRatio="xMidYMid meet" aria-hidden="true" focusable="false">
      <g fill="none" strokeWidth="1">
        {INITIAL_ROUTES.map(route => <path key={route.id} data-route={route.id} data-ecosystem-route={route.id.startsWith('ecosystem-') ? '' : undefined} d={pathData(route)} stroke={route.color} opacity={route.opacity} />)}
        <path d="M-70 714 C230 593 429 789 757 703 S1207 741 1510 707" stroke="#afc9ee" opacity=".3" />
      </g>
      <g>
        {INITIAL_ROUTES.map((route, i) => {
          const p = along(route.points, (i * .173) % 1);
          return <circle key={route.id} data-particle={route.id} data-ecosystem-route={route.id.startsWith('ecosystem-') ? '' : undefined} cx={p.x} cy={p.y} r={i % 3 === 0 ? 3.4 : 2.3} fill={route.color} opacity={i < 6 ? .55 : .8} />;
        })}
      </g>
      <g transform={`translate(${CORE.x} ${CORE.y})`}>
        <circle data-core-orbit r="177" fill="none" stroke="#739dec" strokeWidth=".8" strokeDasharray="1.5 6" strokeOpacity=".35" />
        <circle r="162" fill="none" stroke="#a4c8ef" strokeWidth=".8" strokeOpacity=".55" />
        <circle r="148" fill="#f7faff" fillOpacity=".97" stroke="#719eea" strokeWidth="1.2" />
        <g data-lattice>
          {SYNAPSES.map(({ a, b }, i) => <line key={i} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="#93bced" strokeWidth=".8" strokeOpacity=".38" />)}
          {NEURONS.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r={i % 3 === 0 ? 3.5 : 2.6} fill={COLORS[i % 4]} />)}
        </g>
        <circle r="55" fill="#f7faff" stroke="#afcaee" strokeWidth=".9" />
        <text className="llm-core-label" textAnchor="middle"><tspan x="0" y="-3">TECH</tspan><tspan x="0" y="20">HUB</tspan></text>
      </g>
      {AGENTS.map((p, i) => <g key={i} data-agent={i} data-service={SERVICES[i].label} transform={`translate(${p.x} ${p.y})`}>
        <circle r="88" fill="none" stroke="#8bb4f2" strokeWidth=".8" strokeDasharray="1.5 5" strokeOpacity=".55" />
        <circle className="llm-service-disc" r={SERVICE_RADIUS} fill="#f7faff" stroke="#5688e8" strokeWidth="1.2" />
        <g transform={`translate(0 ${SERVICES[i].lines.length === 3 ? -31 : -24})`} fill="none" stroke="#397be7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <TechnologyGlyph type={SERVICES[i].icon} />
        </g>
        <text className="llm-agent-label" textAnchor="middle">{SERVICES[i].lines.map((line, j) => <tspan key={line} x="0" y={SERVICES[i].lines.length === 1 ? 25 : SERVICES[i].lines.length === 3 ? 8 + j * 19 : 14 + j * 20}>{line}</tspan>)}</text>
      </g>)}
      <g className="llm-ecosystem">
        {ECOSYSTEM.map((p, i) => <g key={i} data-ecosystem={i} transform={`translate(${p.x} ${p.y})`}>
          <circle r="52" fill="none" stroke="#8bb4f2" strokeWidth=".8" strokeDasharray="1.5 5" strokeOpacity=".45" />
          <circle r="40" fill="#f7faff" fillOpacity=".94" stroke="#8daeed" strokeWidth="1" />
          <g fill="none" stroke="#5d8ee0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><TechnologyGlyph type={(['cloud', 'server', 'app'] as Glyph[])[i]} /></g>
        </g>)}
      </g>
    </svg>
  </div>;
}
